import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { GetAllSponsorActivitiesAPI } from "@/services/api/sponsor/getAllSponsorActivities";
import { GetSponsorTasksByUserAPI } from "@/services/api/sponsor/getSponsorsByUser";
import { compensationApproveAPI, compensationRejectAPI } from "@/services/api/sponsor/compensationApi";
import { approveOrRejectSponsorTaskCompletion } from "@/services/api/sponsor/sponsorOwnerCompletionsApi";
import { getActivityDeadline, getActivityDeadlineString } from "@/utils/deadline";
import {
  hasProof,
  getCompletionStatus,
  getActivityHasPreDeadlineProof,
  canAttemptActivity,
  getSponsorProofIgAccounts,
  getSponsorProofMedia,
  getSponsorProofRegisteredUsernames,
} from "@/utils/sponsorStatus";
import Icon from "@/components/Icon";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import AdminEmptyState from "@/components/AdminEmptyState";
import Modal from "@/components/Modal";

const BOT_CHECKED_TASK_TYPES = ["register_app", "ensure_followers"];

/** Resolve the sponsor task type from whichever field the API provides. */
const getTaskType = (task: any): string | undefined =>
  task?.sponsor_task_type ?? task?.sponsor_task?.type ?? task?.task_type ?? task?.type;

/** Status helpers */
const getTaskStatusInfo = (task: any, isBotChecked: boolean, isWindowClosed: boolean) => {
  const qjs = task.queue_job_status as string | undefined;
  if (qjs === "completed" || task.completed)
    return { label: "Completed", color: "#4caf50", icon: "check-circle", bg: "#e8f5e9" };
  if (typeof task.review_count === "number" && task.review_count >= 3)
    return {
      label: isBotChecked ? "Auto-failed by bot" : "Auto-failed",
      color: "#f44336",
      icon: "close-circle",
      bg: "#ffebee",
    };
  if (qjs === "ready")
    return { label: "Awaiting check", color: "#ff9800", icon: "clock-outline", bg: "#fff8e1" };
  if (qjs === "processing")
    return { label: "Reviewing", color: "#ff9800", icon: "sync", bg: "#fff3e0" };
  if (qjs === "failed") {
    if (isWindowClosed)
      return { label: "Failed", color: "#f44336", icon: "close-circle", bg: "#ffebee" };
    return { label: "Awaiting re-submit", color: "#ff9800", icon: "alert-circle", bg: "#fff3e0" };
  }
  if (hasProof(task))
    return { label: "Proof submitted", color: "#ff9800", icon: "clock-outline", bg: "#fff8e1" };
  return { label: "Pending", color: "#999", icon: "clock-outline", bg: "#f5f5f5" };
};

/**
 * Renderable rows for a task's proof.
 *
 * Delegates to the shared proof-shape helpers. This used to sniff `media` and
 * `ig_accounts` by hand and returned nothing for a `register_app` proof, so a
 * student who submitted usernames looked like they had submitted nothing.
 */
const getProofMedia = (task: any): { type: string; url: string }[] => {
  const media = getSponsorProofMedia(task?.proof).filter((p: any) => p && p.url);
  if (media.length > 0) return media;
  return [
    ...getSponsorProofIgAccounts(task?.proof).map((acc: string) => ({
      type: "ig_account",
      url: acc,
    })),
    ...getSponsorProofRegisteredUsernames(task?.proof).map((name: string) => ({
      type: "username",
      url: name,
    })),
  ];
};

/**
 * Admin sponsor task check screen. Two modes:
 *  - `/console/sponsors/:activityId` – review one activity's tasks
 *  - `/console/sponsors/user/:userId?sponsorId=` – review a user's tasks (optionally per sponsor)
 */
const SponsorCheckPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activityId, userId } = useParams<{ activityId?: string; userId?: string }>();
  const [searchParams] = useSearchParams();
  const sponsorId = searchParams.get("sponsorId") || undefined;
  const admin = useSelector((state: any) => state.auth.admin);

  const [allTasks, setAllTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityInfo, setActivityInfo] = useState<any>(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [compActionLoading, setCompActionLoading] = useState(false);
  const [taskActionLoading, setTaskActionLoading] = useState<Record<string, boolean>>({});
  const [taskRejectModalVisible, setTaskRejectModalVisible] = useState(false);
  const [taskRejectTarget, setTaskRejectTarget] = useState<{ completionId: string; taskName: string } | null>(null);
  const [taskRejectReason, setTaskRejectReason] = useState("");
  const [taskApproveModalVisible, setTaskApproveModalVisible] = useState(false);
  const [taskApproveTarget, setTaskApproveTarget] = useState<{ completionId: string; taskName: string } | null>(null);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [imageViewerUri, setImageViewerUri] = useState("");
  const [failReasonModalVisible, setFailReasonModalVisible] = useState(false);
  const [failReasonText, setFailReasonText] = useState("");
  const [failReasonTaskName, setFailReasonTaskName] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);

  const fetchByActivityId = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await GetAllSponsorActivitiesAPI(1);
      if (resp.status === 0 && Array.isArray(resp.resp)) {
        const activity = resp.resp.find((a: any) => a.id === activityId);
        if (!activity) {
          setError(t("Activity not found."));
          setLoading(false);
          return;
        }
        setActivityInfo(activity);
        const tasks = (activity.completions || []).map((c: any) => ({
          id: c.id,
          name: c.sponsor_task?.title || "Untitled Task",
          description: "",
          completed: c.completed,
          proof: c.proof,
          review_count: c.review_count,
          queue_job_status: c.queue_job_status,
          sponsor_task_type: c.sponsor_task?.type,
          sponsor: activity.sponsor || {},
        }));
        setAllTasks(tasks);
      } else {
        setError(t("Failed to load activity details."));
      }
    } catch {
      setError(t("Failed to load activity details."));
    } finally {
      setLoading(false);
    }
  };

  const fetchByUserId = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await GetSponsorTasksByUserAPI({ userId: userId!, page: 1 });
      if (resp && resp.status === 0) {
        let tasks = resp.resp || [];
        if (sponsorId) {
          tasks = tasks.filter((task: any) => task.sponsor?.id === sponsorId);
        }
        setAllTasks(tasks);
      } else if (resp && resp.status === -40024) {
        setError(t("Missing userId for sponsor tasks."));
      } else if (resp && resp.status === 2) {
        setError(t("Server error: Failed to fetch sponsor tasks."));
      } else {
        setError(t("Failed to load sponsor tasks."));
      }
    } catch {
      setError(t("Failed to load sponsor tasks."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activityId) {
      fetchByActivityId();
    } else if (userId) {
      fetchByUserId();
    } else {
      setError(t("No userId or activityId provided."));
      setLoading(false);
    }
  }, [activityId, userId, sponsorId]);

  const updateTaskLocal = (completionId: string, updates: Partial<any>) => {
    setAllTasks((prev) => prev.map((task) => (task.id === completionId ? { ...task, ...updates } : task)));
    setActivityInfo((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        completions: (prev?.completions || []).map((c: any) =>
          c.id === completionId ? { ...c, ...updates } : c,
        ),
      };
    });
  };

  const doApproveCompensation = async () => {
    if (!activityInfo?.id) return;
    setCompActionLoading(true);
    try {
      const resp = await compensationApproveAPI({
        sponsorActivityId: activityInfo.id,
        approverId: admin?.id,
      });
      setCompActionLoading(false);
      setApproveModalVisible(false);
      if (resp && resp.status === 0) {
        setActivityInfo((prev: any) => ({ ...prev, compensation_approval_status: "approved" }));
      } else {
        alert(resp?.error || t("Failed to approve."));
      }
    } catch {
      setCompActionLoading(false);
      alert(t("Failed to approve."));
    }
  };

  const doRejectCompensation = async () => {
    if (!activityInfo?.id || !rejectReason.trim()) {
      alert(t("Please enter a reason."));
      return;
    }
    setCompActionLoading(true);
    try {
      const resp = await compensationRejectAPI({
        sponsorActivityId: activityInfo.id,
        approverId: admin?.id,
        compensation_reject_reason: rejectReason.trim(),
      });
      setCompActionLoading(false);
      setRejectModalVisible(false);
      setRejectReason("");
      if (resp && resp.status === 0) {
        setActivityInfo((prev: any) => ({
          ...prev,
          compensation_approval_status: "rejected",
          compensation_reject_reason: rejectReason.trim(),
        }));
      } else {
        alert(resp?.error || t("Failed to reject."));
      }
    } catch {
      setCompActionLoading(false);
      alert(t("Failed to reject."));
    }
  };

  const handleTaskApprove = async (completionId: string) => {
    setTaskActionLoading((prev) => ({ ...prev, [completionId]: true }));
    try {
      const resp = await approveOrRejectSponsorTaskCompletion({
        completionId,
        approve: true,
        userId: admin?.id,
      });
      setTaskActionLoading((prev) => ({ ...prev, [completionId]: false }));
      if (resp && resp.status === 0) {
        updateTaskLocal(completionId, {
          completed: true,
          review_count: (allTasks.find((task) => task.id === completionId)?.review_count || 0) + 1,
          queue_job_status: "completed",
        });
      } else {
        alert(resp?.error || t("Failed to approve."));
      }
    } catch {
      setTaskActionLoading((prev) => ({ ...prev, [completionId]: false }));
      alert(t("Failed to approve."));
    }
  };

  const handleTaskReject = async () => {
    if (!taskRejectTarget || !taskRejectReason.trim()) return;
    setTaskActionLoading((prev) => ({ ...prev, [taskRejectTarget.completionId]: true }));
    try {
      const resp = await approveOrRejectSponsorTaskCompletion({
        completionId: taskRejectTarget.completionId,
        approve: false,
        failReason: taskRejectReason.trim(),
        userId: admin?.id,
      });
      setTaskActionLoading((prev) => ({ ...prev, [taskRejectTarget.completionId]: false }));
      setTaskRejectModalVisible(false);
      setTaskRejectTarget(null);
      setTaskRejectReason("");
      if (resp && resp.status === 0) {
        updateTaskLocal(taskRejectTarget.completionId, {
          completed: false,
          review_count:
            (allTasks.find((task) => task.id === taskRejectTarget.completionId)?.review_count || 0) + 1,
          queue_job_status: "failed",
        });
      } else {
        alert(resp?.error || t("Failed to reject."));
      }
    } catch {
      setTaskActionLoading((prev) => ({ ...prev, [taskRejectTarget.completionId]: false }));
      alert(t("Failed to reject."));
    }
  };

  const completedCount = allTasks.filter(
    (task) => task.queue_job_status === "completed" || task.completed,
  ).length;
  const totalCount = allTasks.length;
  const pct = totalCount > 0 ? completedCount / totalCount : 0;

  const renderTaskCard = (task: any, index: number) => {
    const isLoading = taskActionLoading[task.id] || false;
    const taskType = getTaskType(task);
    const isBotChecked = typeof taskType === "string" && BOT_CHECKED_TASK_TYPES.includes(taskType);
    const qjs = task.queue_job_status as string | undefined;
    const isCompleted = qjs === "completed" || task.completed === true;
    const hasReviewCount = typeof task.review_count === "number" && task.review_count >= 3;
    const isProcessing = qjs === "processing";
    const canReview = !isCompleted && !hasReviewCount && !isBotChecked && isProcessing;
    const proofMedia = getProofMedia(task);
    const hasSubmittedProof = proofMedia.length > 0;
    const deadlineStr = activityInfo?.activity_date
      ? getActivityDeadlineString(activityInfo.activity_date)
      : null;
    const isWindowClosed = canAttemptActivity(allTasks, deadlineStr) === false;
    const statusInfo = getTaskStatusInfo(task, isBotChecked, isWindowClosed);
    const attemptCount = task.review_count || 0;
    const failReason = task.proof?.fail_reason;

    return (
      <div key={task.id} className="mb-2.5 rounded-xl bg-white p-4 shadow-sm">
        {/* Task Header */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: statusInfo.color + "18" }}
            >
              <span className="text-[13px] font-bold" style={{ color: statusInfo.color }}>
                {index + 1}
              </span>
            </div>
            <span
              className="flex-1 text-[15px] font-semibold leading-5"
              style={{ color: isCompleted ? "#4caf50" : "#222" }}
            >
              {task.name}
            </span>
          </div>
          <div
            className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1"
            style={{ backgroundColor: statusInfo.bg }}
          >
            <Icon iconType="MaterialCommunityIcons" name={statusInfo.icon} size={13} color={statusInfo.color} />
            <span className="text-[11px] font-semibold" style={{ color: statusInfo.color }}>
              {t(statusInfo.label)}
            </span>
          </div>
        </div>

        {/* Attempt Progress */}
        <div className="mt-3 flex items-center gap-2 border-t border-[#f0f0f0] pt-3">
          <div className="flex flex-1 items-center">
            {[0, 1, 2].map((i) => {
              const used = i < attemptCount;
              const isLastUsed = used && i === attemptCount - 1;
              const isActive = !isCompleted && i === attemptCount;
              let bg = "#e0e0e0";
              let icon: string | null = null;
              let tappable = false;
              if (isCompleted) {
                if (used && isLastUsed) {
                  bg = "#4caf50";
                  icon = "check";
                } else if (used) {
                  bg = "#f44336";
                  icon = "close";
                }
              } else {
                if (used) {
                  bg = "#f44336";
                  icon = "close";
                  if (isLastUsed && failReason) tappable = true;
                } else if (isActive && isProcessing) {
                  bg = "#ff9800";
                  icon = "sync";
                }
              }
              return (
                <React.Fragment key={i}>
                  <button
                    type="button"
                    className="flex h-5 w-5 items-center justify-center rounded-full"
                    style={{ backgroundColor: bg }}
                    disabled={!tappable}
                    onClick={() => {
                      if (tappable && failReason) {
                        setFailReasonText(failReason);
                        setFailReasonTaskName(task.name);
                        setFailReasonModalVisible(true);
                      }
                    }}
                  >
                    {icon && <Icon iconType="MaterialCommunityIcons" name={icon} size={8} color="#fff" />}
                  </button>
                  {i < 2 && (
                    <div
                      className="mx-[-2px] h-0.5 w-6 rounded"
                      style={{
                        backgroundColor:
                          used && i + 1 < attemptCount
                            ? isCompleted
                              ? i + 1 === attemptCount - 1
                                ? "#4caf50"
                                : "#f44336"
                              : "#f44336"
                            : used && isCompleted && i + 1 === attemptCount - 1
                              ? "#4caf50"
                              : "#e0e0e0",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <span className="text-xs font-semibold text-[#666]">{`${attemptCount}/3`}</span>
          {(qjs === "failed" || hasReviewCount) && failReason && (
            <button
              type="button"
              className="ml-2 flex items-center gap-1"
              onClick={() => {
                setFailReasonText(failReason);
                setFailReasonTaskName(task.name);
                setFailReasonModalVisible(true);
              }}
            >
              <Icon iconType="MaterialCommunityIcons" name="information-outline" size={14} color="#f44336" />
              <span className="text-[11px] font-medium text-[#f44336] underline">{t("Why failed")}</span>
            </button>
          )}
        </div>

        {taskType === "publish_story_post" && activityInfo?.proof_code && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs font-bold text-gray-800">
              {t("Proof Code")}:{" "}
              <span className="tracking-[0.15em] text-[#ffa217]">
                {activityInfo.proof_code}
              </span>
            </p>
            <p className="mt-1 text-[11px] font-semibold text-red-600">
              {t("Reject if the proof code is not clearly visible in the Story/Post.")}
            </p>
            {task.proof?.post_url && (
              <a
                href={task.proof.post_url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-[11px] font-semibold text-blue-600 underline"
              >
                {task.proof.post_url}
              </a>
            )}
          </div>
        )}

        {/* Proof Media */}
        {hasSubmittedProof && (
          <div className="mt-3 border-t border-[#f0f0f0] pt-3">
            <div className="mb-1.5 text-xs font-bold text-[#666]">{t("Proof")}</div>
            <div className="flex flex-wrap gap-2">
              {proofMedia.map((media: any, idx: number) => {
                if (media.type === "ig_account") {
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-1 rounded-full bg-[#f5f5f5] px-2.5 py-1.5"
                    >
                      <Icon iconType="MaterialCommunityIcons" name="instagram" size={12} color="#E4405F" />
                      <span className="text-xs font-medium text-[#333]">{media.url}</span>
                    </div>
                  );
                }
                const isVideo = media.type === "video" || (media.url && media.url.endsWith(".mp4"));
                return (
                  <button
                    key={idx}
                    type="button"
                    className="relative h-20 w-20 overflow-hidden rounded-lg bg-[#f0f0f0]"
                    onClick={() => {
                      if (isVideo) {
                        window.open(media.url, "_blank");
                      } else {
                        setImageViewerUri(media.url);
                        setImageViewerVisible(true);
                      }
                    }}
                  >
                    <img src={media.url} alt="" className="h-full w-full object-cover" />
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Icon iconType="MaterialCommunityIcons" name="play-circle" size={28} color="#fff" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bot Badge */}
        {isBotChecked && (
          <div className="mt-2">
            {isCompleted ? (
              <div className="flex items-center gap-1.5 self-start rounded-md bg-[#e8f5e9] px-2.5 py-1.5">
                <Icon iconType="MaterialCommunityIcons" name="check-circle" size={14} color="#4caf50" />
                <span className="text-[11px] font-semibold text-[#4caf50]">{t("Verified by bot")}</span>
              </div>
            ) : hasReviewCount ? (
              <div className="flex items-center gap-1.5 self-start rounded-md bg-[#ffebee] px-2.5 py-1.5">
                <Icon iconType="MaterialCommunityIcons" name="close-circle" size={14} color="#f44336" />
                <span className="text-[11px] font-semibold text-[#f44336]">{t("Auto-failed by bot")}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 self-start rounded-md bg-[#f3f4f6] px-2.5 py-1.5">
                <Icon iconType="MaterialCommunityIcons" name="robot" size={14} color="#6b7280" />
                <span className="text-[11px] font-semibold text-[#6b7280]">{t("Auto-checked by bot")}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {canReview && (
          <div className="mt-3 flex gap-2 border-t border-[#f0f0f0] pt-3">
            <button
              type="button"
              className={`flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#4caf50] py-2.5 text-sm font-semibold text-white transition hover:opacity-90 ${
                isLoading ? "opacity-50" : ""
              }`}
              onClick={() => {
                setTaskApproveTarget({ completionId: task.id, taskName: task.name });
                setTaskApproveModalVisible(true);
              }}
              disabled={isLoading}
            >
              <Icon iconType="MaterialCommunityIcons" name="check" size={16} color="#fff" />
              {isLoading ? "..." : t("Approve")}
            </button>
            <button
              type="button"
              className={`flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#f44336] py-2.5 text-sm font-semibold text-white transition hover:opacity-90 ${
                isLoading ? "opacity-50" : ""
              }`}
              onClick={() => {
                setTaskRejectTarget({ completionId: task.id, taskName: task.name });
                setTaskRejectModalVisible(true);
              }}
              disabled={isLoading}
            >
              <Icon iconType="MaterialCommunityIcons" name="close" size={16} color="#fff" />
              {t("Reject")}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-full flex-col p-4">
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#e0e0e0] bg-white transition hover:bg-gray-50"
          onClick={() => navigate(-1)}
        >
          <Icon iconType="MaterialCommunityIcons" name="chevron-left" size={22} color="#333" />
        </button>
        <h1 className="text-2xl font-bold text-[#222]">{t("Sponsor Tasks")}</h1>
      </div>

      {loading ? (
        <AdminListSkeleton count={3} />
      ) : error ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="cloud-alert-outline"
          title={t("Something went wrong")}
          message={error}
          actionLabel={t("Retry")}
          onAction={() => {
            if (activityId) fetchByActivityId();
            else if (userId) fetchByUserId();
          }}
        />
      ) : (
        <div className="w-full">
          {/* Activity Info */}
          {activityInfo && (
            <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="rounded-md px-2 py-0.5 text-[10px] font-bold text-white"
                  style={{ backgroundColor: activityInfo.type === "society" ? "#7c4dff" : "#2196f3" }}
                >
                  {activityInfo.type === "society" ? t("Society") : t("Personal")}
                </span>
                {activityInfo.activity_date && (
                  <div className="flex items-center gap-1">
                    <Icon iconType="MaterialCommunityIcons" name="calendar" size={12} color="#888" />
                    <span className="text-[11px] text-[#888]">{activityInfo.activity_date}</span>
                  </div>
                )}
              </div>
              {activityInfo.student && (
                <div className="mb-1 flex items-center gap-1.5">
                  <Icon iconType="MaterialCommunityIcons" name="account" size={14} color="#666" />
                  <span className="text-[13px] font-medium text-[#333]">
                    {activityInfo.student.username || "-"}
                  </span>
                </div>
              )}
              {activityInfo.society && (
                <div className="mb-1 flex items-center gap-1.5">
                  <Icon iconType="MaterialCommunityIcons" name="google-my-business" size={14} color="#666" />
                  <span className="text-[13px] font-medium text-[#333]">
                    {activityInfo.society.name || "-"}
                  </span>
                </div>
              )}
              {activityInfo.signed_agreement && (
                <button
                  type="button"
                  className="mt-2.5 mb-1 flex items-center justify-center gap-1 rounded-lg border border-[#ffa217] px-2.5 py-1.5"
                  onClick={() => setPdfModalVisible(true)}
                >
                  <Icon iconType="MaterialCommunityIcons" name="file-pdf-box" size={14} color="#ffa217" />
                  <span className="text-xs font-medium text-[#ffa217]">{t("View Agreement")}</span>
                </button>
              )}

              {/* Progress */}
              <div className="mt-3 flex items-center gap-2">
                <span className="min-w-[28px] text-xs font-bold text-[#333]">{`${completedCount}/${totalCount}`}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded bg-[#f0f0f0]">
                  <div
                    className="h-full rounded"
                    style={{
                      width: `${pct * 100}%`,
                      backgroundColor: pct === 1 ? "#4caf50" : "#ffa217",
                    }}
                  />
                </div>
                <span className="min-w-[32px] text-right text-xs font-bold text-[#333]">
                  {`${Math.round(pct * 100)}%`}
                </span>
              </div>

              {/* Compensation */}
              {(() => {
                const allTasksList = activityInfo.completions || [];
                const hasCompensation = !!activityInfo.compensation_amount;
                const hasReceipt = !!activityInfo.compensation_receipt_url;
                const approvalStatus = activityInfo.compensation_approval_status;
                const deadline = getActivityDeadline(activityInfo.activity_date);
                let deadlineEnd: Date | null = null;
                let cureEnd: Date | null = null;
                if (deadline) {
                  deadlineEnd = new Date(deadline.getTime());
                  deadlineEnd.setHours(23, 59, 59, 999);
                  cureEnd = new Date(deadlineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);
                }
                const hasPreDeadlineProof = getActivityHasPreDeadlineProof(allTasksList, deadlineEnd);
                const numFailed = allTasksList.filter((c: any) =>
                  getCompletionStatus(c, hasPreDeadlineProof, deadlineEnd, cureEnd) === "failed",
                ).length;
                const showCompensation = numFailed > 0 && !!deadlineEnd && new Date() > deadlineEnd;
                if (!showCompensation) return null;

                return (
                  <div className="mt-2.5 rounded-lg border border-[#f0f0f0] bg-[#fafafa] p-2.5">
                    <div className="mb-1.5 flex items-center gap-1">
                      <Icon
                        iconType="MaterialCommunityIcons"
                        name={hasCompensation ? "alert-circle" : "clock-outline"}
                        size={14}
                        color={hasCompensation ? "#f44336" : "#ff9800"}
                      />
                      <span
                        className="text-[13px] font-bold"
                        style={{ color: hasCompensation ? "#f44336" : "#ff9800" }}
                      >
                        {hasCompensation
                          ? t("HKD ${{amount}}", {
                              amount: Number(activityInfo.compensation_amount).toLocaleString(),
                            })
                          : t("Pending")}
                      </span>
                    </div>
                    {hasReceipt && (
                      <div>
                        <button
                          type="button"
                          className="block w-full overflow-hidden rounded-md"
                          onClick={() => {
                            setImageViewerUri(activityInfo.compensation_receipt_url);
                            setImageViewerVisible(true);
                          }}
                        >
                          <img
                            src={activityInfo.compensation_receipt_url}
                            alt="receipt"
                            className="h-[140px] w-full object-cover"
                          />
                        </button>
                        {approvalStatus === "pending" && (
                          <div className="mt-2 flex gap-1.5">
                            <button
                              type="button"
                              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[#4caf50] py-2 text-xs font-semibold text-white"
                              onClick={() => setApproveModalVisible(true)}
                              disabled={compActionLoading}
                            >
                              <Icon iconType="MaterialCommunityIcons" name="check" size={14} color="#fff" />
                              {t("Approve")}
                            </button>
                            <button
                              type="button"
                              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[#f44336] py-2 text-xs font-semibold text-white"
                              onClick={() => setRejectModalVisible(true)}
                              disabled={compActionLoading}
                            >
                              <Icon iconType="MaterialCommunityIcons" name="close" size={14} color="#fff" />
                              {t("Reject")}
                            </button>
                          </div>
                        )}
                        {approvalStatus === "approved" && (
                          <div className="mt-1.5 flex items-center gap-1 self-start rounded-md bg-[#e8f5e9] px-2 py-1">
                            <Icon iconType="MaterialCommunityIcons" name="check-circle" size={12} color="#4caf50" />
                            <span className="text-[11px] font-semibold text-[#4caf50]">{t("Approved")}</span>
                          </div>
                        )}
                        {approvalStatus === "rejected" && (
                          <div className="mt-1.5 flex items-center gap-1 self-start rounded-md bg-[#ffebee] px-2 py-1">
                            <Icon iconType="MaterialCommunityIcons" name="close-circle" size={12} color="#f44336" />
                            <span className="text-[11px] font-semibold text-[#f44336]">{t("Rejected")}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tasks List */}
          <div className="mb-2.5">
            <span className="text-base font-bold text-[#222]">{`${t("Tasks")} (${totalCount})`}</span>
          </div>
          {allTasks.length === 0 ? (
            <div className="mt-10 text-center text-[15px] text-[#888]">{t("no_sponsor_tasks_found")}</div>
          ) : (
            allTasks.map((task, idx) => renderTaskCard(task, idx))
          )}
        </div>
      )}

      {/* Image Viewer */}
      <Modal visible={imageViewerVisible} onClose={() => setImageViewerVisible(false)}>
        <div className="relative flex max-h-[90vh] max-w-[90vw] items-center justify-center">
          <button
            type="button"
            className="absolute -top-10 right-0 p-2"
            onClick={() => setImageViewerVisible(false)}
          >
            <Icon iconType="MaterialCommunityIcons" name="close" size={28} color="#fff" />
          </button>
          <img src={imageViewerUri} alt="" className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain" />
        </div>
      </Modal>

      {/* Approve Receipt Modal */}
      <Modal visible={approveModalVisible} onClose={() => setApproveModalVisible(false)}>
        <div className="w-[85%] max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#e8f5e9]">
            <Icon iconType="MaterialCommunityIcons" name="check" size={28} color="#4caf50" />
          </div>
          <div className="mb-1 text-base font-bold text-[#222]">{t("Approve Receipt")}</div>
          <div className="mb-3.5 text-[13px] text-[#888]">
            {t("Approve this compensation receipt?")}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => setApproveModalVisible(false)}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#4caf50] py-3 text-sm font-semibold text-white"
              onClick={doApproveCompensation}
              disabled={compActionLoading}
            >
              {compActionLoading ? "..." : t("Approve")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reject Receipt Modal */}
      <Modal visible={rejectModalVisible} onClose={() => setRejectModalVisible(false)} position="bottom">
        <div className="rounded-t-[20px] bg-white p-6 pb-8 shadow-lg">
          <div className="mx-auto mb-4 h-1 w-10 rounded bg-[#ddd]" />
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ffebee]">
            <Icon iconType="MaterialCommunityIcons" name="close" size={28} color="#f44336" />
          </div>
          <div className="mb-1 text-center text-base font-bold text-[#222]">{t("Reject Receipt")}</div>
          <textarea
            className="mb-4 mt-2 min-h-[90px] w-full rounded-[10px] border-[1.5px] border-[#e0e0e0] bg-[#fafafa] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("Reason...")}
            maxLength={200}
          />
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => {
                setRejectModalVisible(false);
                setRejectReason("");
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className={`flex-1 rounded-[10px] bg-[#f44336] py-3 text-sm font-semibold text-white ${
                !rejectReason.trim() || compActionLoading ? "opacity-50" : ""
              }`}
              onClick={doRejectCompensation}
              disabled={compActionLoading || !rejectReason.trim()}
            >
              {compActionLoading ? "..." : t("Reject")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Task Reject Modal */}
      <Modal visible={taskRejectModalVisible} onClose={() => setTaskRejectModalVisible(false)} position="bottom">
        <div className="rounded-t-[20px] bg-white p-6 pb-8 shadow-lg">
          <div className="mx-auto mb-4 h-1 w-10 rounded bg-[#ddd]" />
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ffebee]">
            <Icon iconType="MaterialCommunityIcons" name="close" size={28} color="#f44336" />
          </div>
          <div className="mb-1 text-center text-base font-bold text-[#222]">
            {taskRejectTarget?.taskName || ""}
          </div>
          <textarea
            className="mb-4 mt-2 min-h-[90px] w-full rounded-[10px] border-[1.5px] border-[#e0e0e0] bg-[#fafafa] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
            value={taskRejectReason}
            onChange={(e) => setTaskRejectReason(e.target.value)}
            placeholder={t("Reason...")}
            maxLength={200}
          />
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => {
                setTaskRejectModalVisible(false);
                setTaskRejectTarget(null);
                setTaskRejectReason("");
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className={`flex-1 rounded-[10px] bg-[#f44336] py-3 text-sm font-semibold text-white ${
                !taskRejectReason.trim() ||
                (taskRejectTarget && taskActionLoading[taskRejectTarget.completionId])
                  ? "opacity-50"
                  : ""
              }`}
              onClick={handleTaskReject}
              disabled={
                !taskRejectReason.trim() ||
                (taskRejectTarget ? !!taskActionLoading[taskRejectTarget.completionId] : false)
              }
            >
              {taskRejectTarget && taskActionLoading[taskRejectTarget.completionId]
                ? "..."
                : t("Reject")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Task Approve Modal */}
      <Modal visible={taskApproveModalVisible} onClose={() => setTaskApproveModalVisible(false)} position="bottom">
        <div className="rounded-t-[20px] bg-white p-6 pb-8 shadow-lg">
          <div className="mx-auto mb-4 h-1 w-10 rounded bg-[#ddd]" />
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#e8f5e9]">
            <Icon iconType="MaterialCommunityIcons" name="check" size={28} color="#4caf50" />
          </div>
          <div className="mb-1 text-center text-base font-bold text-[#222]">
            {taskApproveTarget?.taskName || ""}
          </div>
          <div className="mb-3.5 text-center text-[13px] text-[#888]">
            {t("Are you sure you want to approve this task?")}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => {
                setTaskApproveModalVisible(false);
                setTaskApproveTarget(null);
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className={`flex-1 rounded-[10px] bg-[#4caf50] py-3 text-sm font-semibold text-white ${
                taskApproveTarget && taskActionLoading[taskApproveTarget.completionId]
                  ? "opacity-50"
                  : ""
              }`}
              onClick={async () => {
                if (!taskApproveTarget) return;
                setTaskApproveModalVisible(false);
                await handleTaskApprove(taskApproveTarget.completionId);
                setTaskApproveTarget(null);
              }}
              disabled={
                taskApproveTarget ? !!taskActionLoading[taskApproveTarget.completionId] : false
              }
            >
              {taskApproveTarget && taskActionLoading[taskApproveTarget.completionId]
                ? "..."
                : t("Approve")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Fail Reason Modal */}
      <Modal visible={failReasonModalVisible} onClose={() => setFailReasonModalVisible(false)}>
        <div className="w-[85%] max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#ffebee]">
            <Icon iconType="MaterialCommunityIcons" name="alert-circle" size={28} color="#f44336" />
          </div>
          <div className="mb-1 text-base font-bold text-[#222]">{failReasonTaskName}</div>
          <div className="mb-3.5 text-left text-[13px] text-[#333]">{failReasonText}</div>
          <button
            type="button"
            className="w-full rounded-[10px] bg-[#ffa217] py-3 text-sm font-semibold text-white"
            onClick={() => setFailReasonModalVisible(false)}
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      {/* Agreement PDF Modal */}
      {activityInfo && (
        <Modal visible={pdfModalVisible} onClose={() => setPdfModalVisible(false)} fullScreen>
          <div className="flex h-full flex-col bg-[#ffa217]">
            <div className="flex items-center justify-between px-4 py-2">
              <span className="text-sm font-semibold text-white">{t("View Agreement")}</span>
              <button
                type="button"
                className="rounded-full bg-white/85 p-1.5"
                onClick={() => setPdfModalVisible(false)}
              >
                <Icon iconType="MaterialCommunityIcons" name="chevron-down" size={28} color="#222" />
              </button>
            </div>
            {/*
              The browser renders the PDF itself rather than through a third-party
              viewer. This used to be an iframe on `docs.google.com/gview`, which is
              a page on Google's infrastructure that re-fetches the signed agreement
              on every view: it cannot load at all where `docs.google.com` is
              unreachable, and when it cannot fetch the document it answers with a
              bare "No preview available" that the page cannot detect — a dead URL
              and a live one look identical from here. `<object>` points at the
              document we control and is rendered natively, and per the HTML spec it
              shows its fallback children when the document cannot be instantiated
              at all and fires `error` when it cannot be fetched, so the failure is
              honest without any state to keep in sync.
            */}
            <object
              data={activityInfo.signed_agreement}
              type="application/pdf"
              title="agreement"
              className="flex-1 bg-white"
            >
              <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#ffa217] px-6 text-center">
                <p className="text-sm font-bold text-white">
                  {t("The signed agreement could not be displayed.")}
                </p>
                <a
                  href={activityInfo.signed_agreement}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-white/90 px-3 py-2 text-xs font-bold text-gray-900 shadow"
                >
                  {t("Open the agreement in a new tab")}
                </a>
              </div>
            </object>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SponsorCheckPage;